import { supabase } from '../supabase';
import { inboxStorageUrl } from '../utils/inbox-storage-url';
import { InboxRequestError } from '../utils/inbox-request-error';

export type InboxStatus =
  | 'quarantined'
  | 'ready'
  | 'reviewing'
  | 'saving'
  | 'completed'
  | 'duplicate'
  | 'rejected';
export interface InboxFile {
  id: string;
  file_name: string;
  file_size: number;
  sender: string;
  subject: string;
  received_at: string;
  status: InboxStatus;
  scan_status: 'pending' | 'scanning' | 'retry' | 'clean' | 'infected' | 'failed' | 'skipped' | null;
  can_retry_scan?: boolean;
  reason: string | null;
  archived_at: string | null;
  settlement_id: number | null;
  lease_until: string | null;
  can_restart?: boolean;
  is_processed: boolean;
}
export interface InboxClaim {
  id: string;
  token: string;
  file: File;
}
export interface InboxList {
  files: InboxFile[];
  total: number;
  pending: number;
  pending_sender_count?: number;
  addresses: Array<{ address: string }>;
  sync: { last_success_at: string | null; last_error: string | null };
}

export interface InboxAddress {
  address: string;
  exporter_id: number;
  active: boolean;
  created_at: string;
  provider: 'manual' | 'google_group';
  setup_status: 'provisioning' | 'pending_verification' | 'ready' | 'error';
  setup_error: string | null;
  verification_subject: string | null;
  can_dismiss?: boolean;
}

export interface InboxAddressList {
  addresses: InboxAddress[];
  total: number;
  adminConfigured: boolean;
  addressPrefix?: string;
}

export interface InboxSenderReview {
  pending: Array<{
    gmail_id: string;
    sender_email: string | null;
    sender_display: string;
    subject: string;
    received_at: string;
    attachment_names: string[];
    auth_result: 'pass' | 'review';
  }>;
  senders: Array<{
    email: string;
    status: 'approved' | 'blocked';
    allow_unverified?: boolean;
    updated_at: string;
  }>;
  canManage: boolean;
}

export async function syncInbox() {
  const { data, error } = await supabase.functions.invoke(
    'settlement-inbox-sync',
    { body: {} }
  );
  if (error || data?.error) throw new Error('INBOX_SYNC_FAILED');
  return data as {
    imported: number;
    failed: number;
    more: boolean;
    skipped?: boolean;
  };
}

export async function inboxRequest<T>(
  action: string,
  data: Record<string, unknown> = {}
): Promise<T> {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) throw new InboxRequestError('AUTH_REQUIRED', 401);
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/settlement-inbox`,
    {
      method: 'POST',
      ...(action === 'summary' ? { signal: AbortSignal.timeout(15_000) } : {}),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionData.session.access_token}`,
      },
      body: JSON.stringify({ ...data, action }),
    }
  );
  const result = await response.json().catch(() => null);
  if (!response.ok) throw new InboxRequestError(result?.error || 'INBOX_REQUEST_FAILED', response.status);
  if (!result) throw new Error('INBOX_INVALID_RESPONSE');
  if (!['summary', 'sender-review', 'admin-addresses', 'preview'].includes(action))
    window.dispatchEvent(new Event('settlement-inbox-changed'));
  if (action === 'preview' && typeof result.url === 'string')
    result.url = inboxStorageUrl(result.url, import.meta.env.VITE_SUPABASE_URL);
  return result;
}

export async function claimInboxFile(
  id: string,
  restart = false
): Promise<InboxClaim> {
  const claim = await inboxRequest<{
    id: string;
    token: string;
    url: string;
    fileName: string;
    mimeType: string;
  }>('claim', { id, restart });
  try {
    const response = await fetch(
      inboxStorageUrl(claim.url, import.meta.env.VITE_SUPABASE_URL)
    );
    if (!response.ok) throw new Error('INBOX_FILE_UNAVAILABLE');
    const blob = await response.blob();
    return {
      id,
      token: claim.token,
      file: new File([blob], claim.fileName, { type: claim.mimeType }),
    };
  } catch (error) {
    await inboxRequest('release', { id, token: claim.token }).catch(
      () => undefined
    );
    throw error;
  }
}
